import { Snackbar } from '../../../../ui/components/pure/snackbar/snackbar.component';
import {
  IntentToOpen,
  OpenAllHubActionHandlers,
  OpenAllHubActionProps,
  OpenAllHubActionStates,
  OpenRequestFailed,
  OpenRequestSuccess,
  useOpenAllHubAction
} from '../hooks/open-all-hub-action.hook';
import { DialogActions, DialogContent } from '@mui/material';
import { Alert, AlertTitle } from '@mui/material';
import { TextButton } from '../../../../ui/components/pure/text-button/text-button.component';
import { parseErrorMessage } from '../../../../core/api/api.context';
import { Dropdown } from '../../../../ui/components/pure/dropdown/dropdown.component';
import { includeKioskOptions } from '../open-all-hub-action.constants';
import { Modal } from '../../../../ui/components/pure/modal/modal.component';
import {
  ConfirmationNote,
  ConfirmationNoteForm
} from '../../../../ui/components/pure/modal-content/confirmation-modal.component';
import { UseFormReturn } from 'react-hook-form';
import { KnownOpenAllResponseDtoStatus } from '@groundfloor-platform/mgmt-service-g-sdk';

export function OpenAllHubModal({ hubID, onAbort, ...rest }: OpenAllHubActionProps) {
  const { state, handlers, multipleSides, isLoading, isError, noteForm } = useOpenAllHubAction({
    hubID,
    onAbort,
    ...rest
  });

  if (isError) {
    return (
      <Snackbar severity="error" onClose={() => onAbort()}>
        Sorry, there was an error opening the doors on this hub.{' '}
      </Snackbar>
    );
  }

  return (
    <OpenAllHubModalComponent
      state={state}
      handlers={handlers}
      multipleSides={multipleSides}
      isLoading={isLoading || state.loading}
      noteForm={noteForm}
    />
  );
}

function OpenAllHubModalComponent({
  state,
  handlers,
  multipleSides,
  isLoading,
  noteForm
}: {
  state: OpenAllHubActionStates;
  handlers: OpenAllHubActionHandlers;
  multipleSides?: {
    sideOptions: { label: string; value: string }[];
    onSelectSide(positionDescription: string): void;
  };
  isLoading: boolean;
  noteForm: UseFormReturn<ConfirmationNote, any, ConfirmationNote>;
}) {
  if (state.current === 'OPEN_REQUEST_INTENT_PENDING') {
    return (
      <Modal title={'Open All Lockers'} loading={isLoading} open={true} maxWidth="xs" onClose={handlers.onCancelIntent}>
        <OpenAllHubIntentView multipleSides={multipleSides} {...handlers} {...state} noteForm={noteForm} />
      </Modal>
    );
  }
  if (state.current === 'OPEN_REQUEST_FAILED') {
    return (
      <Modal title={'Open All Lockers'} loading={isLoading} open={true} maxWidth="xs" onClose={handlers.onCancelIntent}>
        <OpenAllHubFailedView {...handlers} {...state} />
      </Modal>
    );
  }
  if (state.current === 'OPEN_REQUEST_SUCCESS') {
    return (
      <Modal title={'Open All Lockers'} loading={isLoading} open={true} maxWidth="xs" onClose={handlers.onCancelIntent}>
        <OpenAllHubSuccessView {...handlers} {...state} />
      </Modal>
    );
  }
  return null;
}

function OpenAllHubIntentView({
  error,
  multipleSides,
  onCancelIntent,
  onSendRequest,
  onIncludeKioskDoorInput,
  noteForm
}: {
  multipleSides?: {
    sideOptions: { label: string; value: string }[];
    onSelectSide(positionDescription: string): void;
  };
} & IntentToOpen & {
    noteForm: UseFormReturn<ConfirmationNote, any, ConfirmationNote>;
  } & OpenAllHubActionHandlers) {
  return (
    <>
      <DialogContent style={{ padding: '16px 24px', fontSize: '14px' }}>
        {error && (
          <p>
            <Alert variant="outlined" severity="error">
              <AlertTitle>Something went wrong</AlertTitle>
              {parseErrorMessage(error.message)}
            </Alert>
          </p>
        )}
        <p>
          This should only be done by LastK staff for testing purposes. There must be LastK staff or contractor present
          to ensure security and close doors.
        </p>

        {multipleSides && multipleSides.sideOptions.length ? (
          <Dropdown
            fullWidth
            required
            textFieldDesktopWidth="100%"
            dropdownVariant="floating"
            menuItems={multipleSides.sideOptions}
            placeholder="Side"
            onChange={(e) => multipleSides.onSelectSide(e.target.value)}
            style={{ marginBottom: '16px' }}
          />
        ) : null}
        <Dropdown
          fullWidth
          required
          textFieldDesktopWidth="100%"
          dropdownVariant="floating"
          menuItems={includeKioskOptions}
          placeholder="Open the kiosk door?"
          onChange={(e) => onIncludeKioskDoorInput(!!e.target.value)}
        />
        <ConfirmationNoteForm isPrivateOnly control={noteForm.control} />
      </DialogContent>
      <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '16px 24px' }}>
        <TextButton color="secondary" variant="outlined" onClick={onCancelIntent}>
          Cancel
        </TextButton>
        <TextButton color="danger" variant="outlined" onClick={() => onSendRequest()}>
          Open All
        </TextButton>
      </DialogActions>
    </>
  );
}

function OpenAllHubFailedView({
  openAllResponse,
  error,
  onSendRequest,
  allowRetry
}: OpenRequestFailed & OpenAllHubActionHandlers) {
  let errorTitle = 'Something went wrong';
  let errorMessage = 'There was an unexpected error.';
  let prompt = allowRetry === false ? 'It looks like no doors opened.' : 'It looks like no doors opened. Please retry.';

  if (openAllResponse) {
    if (openAllResponse.failReasons.includes('Broker responded with status 500')) {
      errorTitle = 'Failed to connect to kiosk';
    }

    const jammedCount = openAllResponse.failReasons.filter((reason) => reason === 'jammed').length;
    if (jammedCount > 0) {
      errorTitle = 'Some doors are jammed';
      errorMessage = `${jammedCount} door${jammedCount > 1 ? 's are' : ' is'} jammed.`;
    } else {
      if (openAllResponse.failCount > 0) {
        errorTitle = 'Failed to open some doors';
        errorMessage = `${openAllResponse.failCount} out of ${openAllResponse.tryCount} door${
          openAllResponse.tryCount > 1 ? 's' : ''
        } failed to open.`;
      }
    }

    if (openAllResponse.tryCount > 1) {
      if (openAllResponse.tryCount > openAllResponse.failCount) {
        prompt =
          allowRetry === false
            ? 'It looks like some doors did not open.'
            : 'It looks like some doors did not open. Please retry.';
      } else {
        prompt =
          allowRetry === false ? 'It looks like no doors opened.' : 'It looks like no doors opened. Please retry.';
      }
    }
  }

  // There could be an error after retrying so need to overwrite the response message
  if (error) {
    errorTitle = 'Something went wrong';
    errorMessage = parseErrorMessage(error.message);
  }

  return (
    <>
      <DialogContent style={{ padding: '16px 24px', fontSize: '14px' }}>
        <p>
          <Alert variant="outlined" severity="error">
            <AlertTitle>{errorTitle}</AlertTitle>
            {errorMessage}
          </Alert>
        </p>
        <p>{prompt}</p>
      </DialogContent>
      <DialogActions>
        {(allowRetry !== false && openAllResponse?.status === KnownOpenAllResponseDtoStatus.Failed) ||
        (allowRetry !== false && error) ? (
          <TextButton color="primary" variant="outlined" onClick={() => onSendRequest(openAllResponse?.failedDoorIDs)}>
            Retry
          </TextButton>
        ) : null}
      </DialogActions>
    </>
  );
}

function OpenAllHubSuccessView({ onDone }: OpenRequestSuccess & OpenAllHubActionHandlers) {
  return (
    <>
      <DialogContent style={{ padding: '16px 24px', fontSize: '14px' }}>
        <p>
          <Alert variant="outlined" severity="success">
            <p style={{ margin: 0, height: '100%', display: 'flex', alignItems: 'center', fontSize: '14px' }}>
              Lockers opened
            </p>
          </Alert>
        </p>
      </DialogContent>
      <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '16px 24px' }}>
        <TextButton color="primary" variant="outlined" onClick={() => onDone()}>
          Done
        </TextButton>
      </DialogActions>
    </>
  );
}
